import { useState } from "react";

import { faInbox } from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { find } from "lodash-es";

import { AccessRequestReasonCell } from "src/adminPanel/AccessRequestReasonCell";
import { AccessRequestStatusPill } from "src/adminPanel/AccessRequestStatusPill";
import {
  useAllOrgNames,
  useCreateAccessRequest,
  useMyAccessRequests,
  useRequestableRoles,
  useUpdateAccessRequestStatus,
} from "src/adminPanel/queries";
import { Button } from "src/base-components/Button";
import { DisplayDateTime } from "src/base-components/Date";
import { Select } from "src/base-components/Select";
import { Cell, Header, TableComp } from "src/base-components/Table";
import {
  AccessRequestInDB,
  AccessRequestStatus,
} from "src/clients/taktile-api";
import { Callout } from "src/design-system/Callout";
import { EmptyState } from "src/design-system/EmptyState";
import { Input } from "src/design-system/Input";
import { toastActions } from "src/design-system/Toast/utils";
import { useHasRole } from "src/store/AuthStore";
import { errorMessage } from "src/utils/stringUtils";

const ORGANIZATION_DIMENSION = "organization_id";

const PAGE_SIZE = 20;

const LABEL_CLASS = "mb-1.5 block font-medium text-gray-700 text-sm";

const STATUS_FILTERS: (AccessRequestStatus | "all")[] = [
  "all",
  AccessRequestStatus.PENDING,
  AccessRequestStatus.APPROVED,
  AccessRequestStatus.REJECTED,
  AccessRequestStatus.CANCELLED,
];

const STATUS_FILTER_OPTIONS = STATUS_FILTERS.map((value) => ({
  key: value,
  value,
}));

const columnHelper = createColumnHelper<AccessRequestInDB>();

export const AccessRequester: React.FC = () => {
  const allOrgs = useAllOrgNames();
  const requestableRoles = useRequestableRoles();
  const createAccessRequest = useCreateAccessRequest();
  const updateStatus = useUpdateAccessRequestStatus();
  const canRequest = useHasRole("access_requester");
  const [organizationId, setOrganizationId] = useState("");
  const [requestedRole, setRequestedRole] = useState("");
  const [reason, setReason] = useState("");
  const [accessDurationDays, setAccessDurationDays] = useState("");

  const roles = requestableRoles.data ?? [];

  const selectedRoleName = requestedRole || roles[0]?.name || "";
  const selectedRole = roles.find((role) => role.name === selectedRoleName);
  const requiresOrganization =
    selectedRole?.dimensions.includes(ORGANIZATION_DIMENSION) ?? false;
  const getRoleDisplayName = (roleName: string) =>
    roles.find((role) => role.name === roleName)?.display_name ?? roleName;
  const [statusFilter, setStatusFilter] = useState<AccessRequestStatus | "all">(
    "all",
  );
  const [orgFilter, setOrgFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const myRequests = useMyAccessRequests({
    status: statusFilter === "all" ? undefined : statusFilter,
    organizationId: orgFilter === "all" ? undefined : orgFilter,
    skip: page * PAGE_SIZE,
    limit: PAGE_SIZE,
  });

  const handleStatusFilterChange = (value: AccessRequestStatus | "all") => {
    setStatusFilter(value);
    setPage(0);
  };

  const handleOrgFilterChange = (value: string) => {
    setOrgFilter(value);
    setPage(0);
  };

  const isSubmitDisabled =
    !canRequest ||
    !selectedRoleName ||
    (requiresOrganization && !organizationId);

  const handleSubmit = async () => {
    if (isSubmitDisabled) {
      return;
    }
    try {
      await createAccessRequest.mutateAsync({
        organization_id: organizationId,
        requested_role: selectedRoleName,
        reason: reason.trim() === "" ? undefined : reason.trim(),
        access_duration_days:
          accessDurationDays.trim() === ""
            ? undefined
            : Number(accessDurationDays.trim()),
      });
      setReason("");
      setAccessDurationDays("");
      toastActions.success({ title: "Access request submitted" });
    } catch (error) {
      toastActions.failure({
        title: "Couldn't submit access request",
        description: errorMessage(error),
      });
    }
  };

  const handleCancel = (accessRequestId: string) => async () => {
    if (!canRequest) {
      return;
    }
    try {
      await updateStatus.mutateAsync({
        accessRequestId,
        status: AccessRequestStatus.CANCELLED,
      });
      toastActions.success({ title: "Access request cancelled" });
    } catch (error) {
      toastActions.failure({
        title: "Couldn't cancel access request",
        description: errorMessage(error),
      });
    }
  };

  const getOrgName = (orgId: string) =>
    find(allOrgs.data, { id: orgId })?.name ?? orgId;

  const requests = myRequests.data?.items ?? [];
  const total = myRequests.data?.total ?? 0;
  const hasMore = myRequests.data?.has_more ?? false;

  const lastPage = total === 0 ? 0 : Math.ceil(total / PAGE_SIZE) - 1;
  if (myRequests.data && page > lastPage) {
    setPage(lastPage);
  }

  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = page * PAGE_SIZE + requests.length;

  const roleOptions = roles.map((role) => ({
    key: role.name,
    value: role.display_name,
  }));

  const orgOptions = [...(allOrgs.data ?? [])]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((org) => ({
      key: org.id,
      value: org.name,
    }));

  const orgFilterOptions = [{ key: "all", value: "all" }, ...orgOptions];

  const columns = [
    columnHelper.display({
      id: "organization",
      header: (info) => <Header info={info}>Organization</Header>,
      cell: (info) => (
        <Cell info={info}>{getOrgName(info.row.original.organization_id)}</Cell>
      ),
    }),
    columnHelper.display({
      id: "requestedRole",
      header: (info) => <Header info={info}>Requested role</Header>,
      cell: (info) => (
        <Cell info={info}>
          {getRoleDisplayName(info.row.original.requested_role)}
        </Cell>
      ),
    }),
    columnHelper.display({
      id: "reason",
      header: (info) => <Header info={info}>Reason</Header>,
      cell: (info) => (
        <AccessRequestReasonCell info={info} value={info.row.original.reason} />
      ),
    }),
    columnHelper.display({
      id: "duration",
      header: (info) => <Header info={info}>Duration (days)</Header>,
      cell: (info) => (
        <Cell info={info}>{info.row.original.access_duration_days ?? "-"}</Cell>
      ),
    }),
    columnHelper.display({
      id: "status",
      header: (info) => <Header info={info}>Status</Header>,
      cell: (info) => (
        <Cell info={info}>
          <AccessRequestStatusPill status={info.row.original.status} />
        </Cell>
      ),
    }),
    columnHelper.display({
      id: "created",
      header: (info) => <Header info={info}>Created</Header>,
      cell: (info) => (
        <Cell info={info}>
          <DisplayDateTime value={info.row.original.created_at} />
        </Cell>
      ),
    }),
    columnHelper.display({
      id: "decisionReason",
      header: (info) => <Header info={info}>Decision reason</Header>,
      cell: (info) => (
        <AccessRequestReasonCell
          info={info}
          value={info.row.original.decision_reason}
        />
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: (info) => <Header info={info}>Actions</Header>,
      cell: (info) => {
        const request = info.row.original;
        return (
          <Cell info={info}>
            <Button
              disabled={
                !canRequest ||
                request.status !== AccessRequestStatus.PENDING ||
                updateStatus.isPending
              }
              size="sm"
              variant="secondary"
              onClick={handleCancel(request.id)}
            >
              Cancel
            </Button>
          </Cell>
        );
      },
    }),
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="mx-auto max-w-screen-2xl">
        <div className="mb-8">
          <h1 className="font-bold text-2xl text-gray-900">Request Access</h1>
          <p className="mt-1 text-gray-500 text-sm">
            Request access to a role for an organization.
          </p>
        </div>

        {!canRequest && (
          <div className="mb-6">
            <Callout variant="warning">
              You do not have permission to request access.
            </Callout>
          </div>
        )}

        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>Role</label>
              <Select
                dataLoc="role-select"
                disabled={!canRequest || requestableRoles.isLoading}
                loading={requestableRoles.isLoading}
                options={roleOptions}
                placeholder={
                  requestableRoles.isLoading
                    ? "Loading roles..."
                    : "No requestable roles"
                }
                value={selectedRoleName}
                fullWidth
                onChange={setRequestedRole}
              />
            </div>
            {requiresOrganization && (
              <div>
                <label className={LABEL_CLASS}>Organization</label>
                <Select
                  dataLoc="organization-select"
                  disabled={!canRequest}
                  options={orgOptions}
                  placeholder="Select an organization"
                  value={organizationId}
                  fullWidth
                  onChange={setOrganizationId}
                />
              </div>
            )}
            <div className="col-span-2">
              <label className={LABEL_CLASS}>Reason</label>
              <Input
                dataLoc="reason-input"
                disabled={!canRequest}
                placeholder="Why do you need this access?"
                type="text"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>Access duration (days)</label>
              <Input
                dataLoc="access-duration-input"
                disabled={!canRequest}
                min={1}
                placeholder="e.g. 30"
                type="number"
                value={accessDurationDays}
                onChange={(event) => setAccessDurationDays(event.target.value)}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-end gap-3">
            <Button
              disabled={isSubmitDisabled}
              loading={createAccessRequest.isPending}
              size="sm"
              variant="primary"
              onClick={handleSubmit}
            >
              Request access
            </Button>
          </div>
        </div>

        <h2 className="mb-4 font-bold text-gray-900 text-lg">
          My access requests
        </h2>
        <div className="mb-4 flex flex-wrap gap-4">
          <div className="w-full max-w-xs">
            <label className={LABEL_CLASS}>Filter by status</label>
            <Select
              dataLoc="status-filter"
              options={STATUS_FILTER_OPTIONS}
              value={statusFilter}
              fullWidth
              onChange={(value) =>
                handleStatusFilterChange(value as AccessRequestStatus | "all")
              }
            />
          </div>
          <div className="w-full max-w-xs">
            <label className={LABEL_CLASS}>Filter by organization</label>
            <Select
              dataLoc="organization-filter"
              options={orgFilterOptions}
              value={orgFilter}
              fullWidth
              onChange={handleOrgFilterChange}
            />
          </div>
        </div>
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          {myRequests.isError ? (
            <div className="p-4">
              <Callout variant="error">
                Error loading your access requests
              </Callout>
            </div>
          ) : (
            <TableComp
              columns={columns}
              data={requests}
              dataLoc="my-access-requests-table"
              disableVirtualization
              emptyState={
                myRequests.isLoading ? undefined : (
                  <EmptyState
                    headline="You have no access requests"
                    icon={faInbox}
                  />
                )
              }
              getRowId={(request) => request.id}
              isLoading={myRequests.isLoading}
            />
          )}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <span className="text-gray-500 text-sm">
            {total === 0
              ? "No results"
              : `Showing ${rangeStart}-${rangeEnd} of ${total}`}
          </span>
          <div className="flex items-center gap-2">
            <Button
              disabled={page === 0 || myRequests.isFetching}
              size="sm"
              variant="secondary"
              onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
            >
              Previous
            </Button>
            <Button
              disabled={!hasMore || myRequests.isFetching}
              size="sm"
              variant="secondary"
              onClick={() => setPage((prev) => prev + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
